<template>
  <div id="value-adjustment">
    <h1>{{ $t('VALUE_ADJUSTMENT') }}</h1>

    <div class="filters">
      <div>
        <text-input
          :placeholder="$t('INPUT_CONTRACT_ID_OR_CLIENT_NAME_PHONE_NUMBER_ASSET_DESCRIPTION')"
          v-model="searchInput"
          @keyup.enter="search"
          with-shadow
          borderless
          rounded-edge
        >
          <template v-slot:icon>
            <font-awesome-icon class="icon" :icon="faSearch" />
          </template>
        </text-input>
      </div>

      <div class="filters-button">
        <toggle-button
          class="value-adjustment-btn"
          :text="$t('VALUE_ADJUSTMENT')"
          :khaki="!filters.hasValueAdjustment"
          :pink="filters.hasValueAdjustment"
          @click="() => toggleHasValueAdjustment()"
          v-if="!isMobile"
          :class="{
            'rounded-corner': !isMobile,
          }"
        />

        <icon-button
          v-else
          :size="35"
          :icon="faFunnelDollar"
          @click="toggleHasValueAdjustment"
          :khaki="!filters.hasValueAdjustment"
          :pink="filters.hasValueAdjustment"
        />

        <div class="date">
          <date-filter
            :from="filters.fromDate"
            :to="filters.toDate"
            @filter="setDate"
            blue
            rounded-corner
          />
        </div>
      </div>
    </div>
    <div class="list">
      <table>
        <thead>
          <tr>
            <th colspan="2">{{ $t('CNTRCT_ID') }}</th>
            <th>{{ $t('CREATED_DATE') }}</th>
            <th>{{ $t('MATURE_DATE') }}</th>
            <th>{{ $t('CLIENT') }}</th>
            <th>{{ $t('ASSET') }}</th>
            <th>{{ $t('STOGARE_DATE') }}</th>
            <th>{{ $t('LOAN_AMOUNT') }}</th>
            <th>{{ $t('ADJUST_VALUE') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr :key="key" v-for="(item, key) in items">
            <td
              class="status"
              :class="{
                'pending': (item.status === CONTRACT_STATUSES.PENDING),
                'approved': (item.status === CONTRACT_STATUSES.APPROVED),
                'disbursed': (item.status === CONTRACT_STATUSES.DISBURSED),
                'closed': (item.status === CONTRACT_STATUSES.CLOSED),
                'overdue': (item.status === CONTRACT_STATUSES.OVERDUE),
                'rejected': (item.status === CONTRACT_STATUSES.REJECTED)
              }"
            ><div class="bullet">•</div></td>
            <td
              class="status"
              :class="{
                'pending': (item.status === CONTRACT_STATUSES.PENDING),
                'approved': (item.status === CONTRACT_STATUSES.APPROVED),
                'disbursed': (item.status === CONTRACT_STATUSES.DISBURSED),
                'closed': (item.status === CONTRACT_STATUSES.CLOSED),
                'overdue': (item.status === CONTRACT_STATUSES.OVERDUE),
                'rejected': (item.status === CONTRACT_STATUSES.REJECTED)
              }"
            >
              <div class="mobile-header" v-if="isMobile">{{ $t('CNTRCT_ID') }}:</div>
              <div :class="{ 'mobile-status': isMobile }">
                <router-link :to="applyParams(PATHS.CONTRACT_DETAIL, item.finfluxId)">{{ item.finfluxId }}</router-link>
                <br />
                <small>{{ $t(item.status) }}</small>
              </div>
            </td>
            <td>
              <div><div class="mobile-header" v-if="isMobile">{{ $t('CREATED_DATE') }}:</div></div>
              <div>{{ formatDate(item.submittedOn) }}</div>
            </td>
            <td>
              <div><div class="mobile-header" v-if="isMobile">{{ $t('MATURE_DATE') }}:</div></div>
              <div>{{ formatDate(item.maturesOn) }}</div>
            </td>
            <td>
              <div class="mobile-header" v-if="isMobile">{{ $t('CLIENT') }}:</div>
              <div>{{ item.client.nameVi }}</div>
            </td>
            <td class="asset">
              <div class="mobile-header" v-if="isMobile">{{ $t('ASSET') }}:</div>
              <div v-html="getFullDescription(item.asset)" />
            </td>
            <td>
              <div class="mobile-header" v-if="isMobile">{{ $t('STOGARE_DATE') }}:</div>
              <div :class="{ 'mobile-storagedate': isMobile }">
                {{ formatDate(item.asset.storedOn || item.asset.createdAt) }}
                <br v-if="!isMobile"/>
                <small>{{ moment().diff(item.asset.storedOn || item.asset.createdAt, 'days')}}</small>
              </div>
            </td>
            <td>
              <div class="mobile-header" v-if="isMobile">{{ $t('LOAN_AMOUNT') }}:</div>
              <div>{{ addCommas(item.approvedAmount) }}</div>
            </td>
            <td class="actions"
            >
              <div class="mobile-header" v-if="isMobile">{{ $t('ADJUST_VALUE') }}:</div>
              <div>
                <action-box
                  :contractId="item.id"
                  :diffAmount="item.adjustedAmount"
                  :approvedAmount="item.approvedAmount"
                /></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination
      :page="filters.page"
      :pageSize="filters.pageSize"
      :total="total"
      @pageSizeChange="changePageSize"
      @pageChange="changePage"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';
import { faSearch, faArrowDown, faFunnelDollar } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import { addCommas, formatDate, applyParams, camelToUpperSnake } from '@/helpers';
import { CONTRACT_STATUSES, PATHS } from '@/consts';

import TextInput from '@/components/TextInput';
import ToggleButton from '@/components/ToggleButton';
import Pagination from '@/components/Pagination';
import DateFilter from '@/components/DateFilter';
import IconButton from '@/components/IconButton';

import ActionBox from './ActionBox';

const { useState, useActions } = createNamespacedHelpers('valueAdjustmentList');
const { useState: useAppState } = createNamespacedHelpers('app');

export default {
  name: 'ValueAdjustmentList',
  components: {
    TextInput,
    ToggleButton,
    FontAwesomeIcon,
    Pagination,
    DateFilter,
    ActionBox,
    IconButton,
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('VALUE_ADJUSTMENT');

    return {
      title,
      meta: []
    };
  },
  setup(props, { root }) {
    const { filters, items, total } = useState(['filters', 'items', 'total']);
    const { applyFilter, getContractList } = useActions(['applyFilter', 'getContractList']);

    const { isMobile } = useAppState(['isMobile']);

    const searchInput = ref(filters.value.term);

    const changePageSize = (value) => {
      applyFilter({
        pageSize: value,
        page: 1,
      });
    };

    const changePage = (value) => {
      applyFilter({
        page: value,
      });
    };

    const search = () => {
      applyFilter({
        term: searchInput.value,
      });
    };

    const setDate = (from, to) => {
      applyFilter({
        fromDate: from,
        toDate: to,
        page: 1
      });
    };

    const toggleHasValueAdjustment = () => {
      applyFilter({
        hasValueAdjustment: !filters.value.hasValueAdjustment,
        page: 1
      });
    };

    const getFullDescription = (asset) => {
      let desc = asset.description;

      Object.keys(asset.meta || {}).forEach((key) => {
        if (asset.meta[key]) {
          desc += `<br/><span style="color:#BCBCBC">${root.$t(camelToUpperSnake(key))}:</span> <span>${asset.meta[key]}</span>`;
        }
      });

      return desc;
    };

    onMounted(() => {
      getContractList();
    });

    watch(filters, () => {
      getContractList();
    });

    return {
      faSearch,
      faCheckCircle,
      faArrowDown,
      filters,
      changePageSize,
      changePage,
      search,
      items,
      total,
      searchInput,
      CONTRACT_STATUSES,
      PATHS,
      formatDate,
      applyParams,
      addCommas,
      setDate,
      moment,
      toggleHasValueAdjustment,
      isMobile,
      faFunnelDollar,
      getFullDescription
    };
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#value-adjustment {
  padding: 30px;

  h1 {
    font-size: 28px;
    text-transform: capitalize;
  }

  ul {
    padding-left: 0px;
  }

  li {
    display: inline;

      span {
        text-transform: capitalize;
      }

      span.highlight {
        color: $geranium;
        padding-right: 20px;
      }
  }

  .filters {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 25px;

    .text-input {
      width: 300px;
      .icon {
        top:20px;
      }
    }

    .date {
      margin-left: 10px;
    }

    .value-adjustment-btn {
      margin-left: 10px;
    }

    .filters-button {
      display: flex;
    }
  }

  .list {
    margin-bottom: 20px;
    max-height: calc(100vh - 300px);
    overflow-y: auto;

    table {
      width: 100%;
      border-collapse: inherit;
      border-spacing: 0;

      thead {
        text-transform: uppercase;
        font-weight: bold;
        text-align: center;

        tr {
          background-color: $babyPowder;

          th {
            padding: 15px 10px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;
            position: sticky;
            top: 0;
            z-index: 9;
            background-color: $babyPowder;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;
            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }
          }
        }
      }

      tbody {
        text-align: center;

        tr {
          background-color: $babyPowder;
          cursor: pointer;
          transition: all .2s ease-in-out;

          &:hover {
            background-color: $ghostWhite;
          }

          td {
            padding: 15px 5px;
            border-bottom: 1px solid $alto;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;
              color: $geranium;
            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }

            &.asset {
              max-width: 400px;
              text-align: left;
            }

            a {
              color: inherit;
            }

            &.status {
              color: $independence;
              line-height: 18px;

              .bullet {
                font-size: 36px;
                padding-right: 0;
                padding-left: 10px;
                transform: translateY(-2px);
                vertical-align: middle;
                display: inline-block;
              }

              &.disbursed {
                color: $shamrockGreen;
              }

              &.closed, &.canceled {
                color: $independence;
              }

              &.overdue {
                color: $geranium;
              }

              &.rejected {
                color: $lavenderGrey;
              }

              &.pending {
                color: $yellowCrayola;
              }

              &.approved {
                color: $blueNcs;
              }
            }

            &.asset {
              @media (min-width: 768px) and (max-width: 1366px) {
                max-width: 200px;
              }
            }

            &.actions {
              width: 120px;
            }

          }
        }
      }
    }
  }

  @media (max-width: 767px) {
    padding: 15px;

    table {
      width: auto;
      display: block;

      thead {
        display: block;

        tr {
          position: absolute;
          top: -9999px;
          left: -9999px;
        }
      }

      tbody {
        text-align: start !important;
        display: block;

        tr {
          display: block;

          &:last-child {
            border-bottom: 1px solid $alto;
          }

          td {
            padding: 5px !important;
            display:block;
            border: none;
            position: relative;
            padding-left: 45% !important;
            min-height: 30px;
            border-bottom: unset !important;

            &:first-child {
              border-left: unset !important;
              border-top: 1px solid $alto;
              padding-top: 20px !important;
              border-top-left-radius: unset !important;
              border-bottom-left-radius: unset !important;
            }

            &:last-child {
              border-right: unset !important;
              padding-bottom: 20px !important;
            }

            &::before {
              position: absolute;
              top: 0;
              left: 6px;
              width: 45%;
              padding-right: 10px;
              white-space: nowrap;
              text-align: center;
            }

            .mobile-header {
              position: absolute;
              left: 6px;
              padding-right: 10px;
              white-space: nowrap;
              text-align: center;
              padding-left: 10px;
            }

            .mobile-status {
              display: flex;

              small {
                padding-left: 10px;
              }
            }

            .mobile-storagedate {

              small {
                padding-left: 10px;
              }
            }

            &.actions {
              width: unset !important;
            }
          }
        }
      }
    }

    .filters {
      display: block;
      margin-bottom: 15px;

      .text-input {
        width: 100%;
        .icon {
          top:20px;
        }
      }

      .value-adjustment-btn {
        margin-left: 0px;
      }

      .filters-button {
        display: -webkit-inline-box;
        margin-top: 15px;

      }
    }
  }
}
</style>
